import 'react-toastify/dist/ReactToastify.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { clsx } from 'clsx';
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { BrandedSideNav } from '../v2/js/components/branded-sidenav';
import { ContextBar } from '../v2/js/components/context-bar';
import { Footer } from '../v2/js/components/footer';
import { MobileHeader } from '../v2/js/components/mobile-header';
import { ContextBarProvider } from '../v2/js/contexts/ContextBarContext';
import { APP_BG_COLOR } from '../v2/js/utils/globalStyles';
import { TRANSITION_PADDING_LEFT } from '../v2/js/utils/transitionStyles';
import { OpenAPI } from './api';
import { setQueryClientInstance } from './api/core/request';
import { Session } from './app/components/Session';
import { FormSubmitContext } from './app/contexts/FormSubmitContext';
import { NavigationElementsContext } from './app/contexts/NavigationElementsContext';
import { useSessionContext } from './app/contexts/SessionContext';
import { ScrollToTop } from './utils/scroll';
import SentryBoundary from './utils/SentryBoundary';

OpenAPI.WITH_CREDENTIALS = true;
OpenAPI.CREDENTIALS = 'include';

// Hook to detect if we're on desktop (lg breakpoint and above)
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(min-width: 1024px)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    // Set initial value
    setIsDesktop(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
}

// Small wrapper component to access SessionContext for the main element
const MainContent = ({
  pageWithoutSideNav,
  children,
}: {
  pageWithoutSideNav: boolean;
  children: React.ReactNode;
}) => {
  const { user } = useSessionContext();
  return (
    <main
      className={`tw:flex tw:flex-1 tw:flex-col tw:min-h-0 tw:bg-white tw:shadow-sm tw:overflow-hidden ${!pageWithoutSideNav && user ? 'tw:rounded-tl-lg' : ''}`}
    >
      {children}
    </main>
  );
};

export const App = () => {
  const footerRef = useRef<HTMLElement>(null);
  const navbarRef = useRef<HTMLElement>(null);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isDesktop = useIsDesktop();

  const isOnboardingRoute = location.pathname.includes('onboarding');
  const pageWithoutSideNav =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/verify-email') ||
    location.pathname.startsWith('/password-reset') ||
    location.pathname.startsWith('/forgot-password') ||
    location.pathname.startsWith('/provider-reference-survey') ||
    location.pathname.startsWith('/app/provider/onboarding');

  // Create query client inside component
  const queryClient = useMemo(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: true,
          refetchOnReconnect: true,
          retry: false,
          retryDelay: 100000,
          staleTime: 5 * 60 * 1000, // 5 minutes
        },
      },
    });
    setQueryClientInstance(client);
    return client;
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      const active = document.activeElement as HTMLInputElement | null;
      if (active && active.type === 'number' && active === e.target) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <SentryBoundary>
      <QueryClientProvider client={queryClient}>
        <div className="page-container">
          <ScrollToTop />
          <Session>
            {/* App container - sidenav and main content */}
            <div className="tw:relative tw:flex tw:min-h-screen tw:w-full">
              {/* Mobile Header */}
              {!pageWithoutSideNav && (
                <div className="tw:fixed tw:top-0 tw:left-0 tw:right-0 tw:z-30 tw:lg:hidden">
                  <MobileHeader onMenuClick={() => setMobileMenuOpen(true)} />
                </div>
              )}

              {/* App Navigation - handles both desktop and mobile internally

                  Width/State Management:
                  - BrandedSideNav manages sidebar width and sets --sidebar-width CSS variable
                    based on collapsed state (updates on collapse/expand toggle)
                  - Main content wrapper uses var(--sidebar-width) for paddingLeft to account
                    for the fixed-position sidebar. Since the sidebar is fixed, it's out of flow,
                    so padding is needed to prevent content overlap.

                  Rendering:
                  - Desktop: Fixed positioned sidebar, hidden on mobile via tw:hidden tw:lg:block
                  - Mobile: Dialog overlay component, only renders when mobileOpen is true */}
              {!pageWithoutSideNav && (
                <BrandedSideNav
                  sidebarRef={navbarRef}
                  mobileOpen={mobileMenuOpen}
                  onMobileClose={() => setMobileMenuOpen(false)}
                />
              )}

              {/* Main content - context bar and app content */}
              <ContextBarProvider>
                <div
                  className={
                    'tw:flex tw:flex-1 tw:flex-col tw:min-h-screen tw:w-full' +
                    (!pageWithoutSideNav
                      ? ' tw:bg-brand-black-2 tw:max-lg:pt-14 tw:max-lg:pl-0 ' +
                        TRANSITION_PADDING_LEFT
                      : '')
                  }
                  style={
                    !pageWithoutSideNav && isDesktop
                      ? { paddingLeft: 'var(--sidebar-width, 0rem)' }
                      : undefined
                  }
                >
                  {/* Context bar - hidden on mobile */}
                  {!pageWithoutSideNav && (
                    <div className="tw:sticky tw:top-0 tw:z-20 tw:w-full tw:text-zinc-200 tw:dark:bg-zinc-700 tw:max-lg:hidden">
                      <ContextBar />
                    </div>
                  )}

                  {/* App content */}
                  <MainContent pageWithoutSideNav={pageWithoutSideNav}>
                    <div
                      className={clsx(
                        'tw:flex tw:flex-1 tw:flex-col tw:min-h-0 tw:overflow-auto ',
                        APP_BG_COLOR
                      )}
                    >
                      <NavigationElementsContext.Provider value={{ footerRef, navbarRef }}>
                        <FormSubmitContext.Provider value={{ isSubmitEnabled, setIsSubmitEnabled }}>
                          <Outlet />
                        </FormSubmitContext.Provider>
                      </NavigationElementsContext.Provider>
                    </div>
                  </MainContent>
                  {!isOnboardingRoute && <Footer elementRef={footerRef} />}
                </div>
              </ContextBarProvider>
            </div>
          </Session>
          <ToastContainer
            position="top-center"
            autoClose={3000}
            closeOnClick
            pauseOnFocusLoss
            pauseOnHover
            draggable
          />
        </div>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </SentryBoundary>
  );
};
